import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, useMotionValue, useSpring, useTransform, useMotionTemplate } from "framer-motion";
import { useSound } from "@/lib/SoundContext";
import DoorFront from "@/components/DoorFront";
import OpenFridge from "@/components/OpenFridge";
import FridgeButton from "@/components/FridgeButton";
import { Hint } from "@/components/Bits";
import { SITE } from "@/data/content";

function Scribble({ className, children }) {
  return (
    <svg viewBox="0 0 120 120" className={`absolute w-20 opacity-50 ${className}`}>
      <g stroke="#2B2320" strokeWidth="3.5" fill="none" strokeLinecap="round">
        {children}
      </g>
    </svg>
  );
}

// midnight kitchen silhouettes — revealed only by the flashlight
function KitchenWall() {
  const c1 = "rgba(43,35,32,0.07)";
  const c2 = "rgba(43,35,32,0.13)";
  return (
    <svg className="fixed inset-0 w-full h-full pointer-events-none z-0" viewBox="0 0 1440 800" preserveAspectRatio="xMidYMid slice" aria-hidden="true">
      <g fill={c1}>
        <rect x="0" y="250" width="1440" height="220" fill="rgba(43,35,32,0.05)" />
        {Array.from({ length: 24 }, (_, i) => (
          <rect key={"v" + i} x={i * 62} y="250" width="3" height="220" />
        ))}
        {[320, 390].map((y) => (
          <rect key={"h" + y} x="0" y={y} width="1440" height="3" />
        ))}
      </g>
      <g fill={c2}>
        {/* left shelf with jars */}
        <rect x="60" y="170" width="240" height="16" rx="8" />
        <rect x="92" y="112" width="40" height="56" rx="9" />
        <rect x="152" y="124" width="34" height="44" rx="8" />
        <circle cx="232" cy="146" r="24" />
        {/* hanging utensils */}
        <rect x="130" y="290" width="6" height="86" rx="3" />
        <ellipse cx="133" cy="396" r="22" />
        <rect x="200" y="290" width="6" height="66" rx="3" />
        <path d="M178 362 q28 34 50 0 l-6 38 q-19 14 -38 0 z" />
        {/* counter + cabinets left */}
        <rect x="30" y="540" width="290" height="20" rx="10" />
        <rect x="50" y="560" width="115" height="140" rx="12" />
        <rect x="185" y="560" width="115" height="140" rx="12" />
        {/* window right */}
        <rect x="1160" y="110" width="210" height="240" rx="14" />
        <rect x="1160" y="110" width="210" height="34" rx="14" fill="rgba(43,35,32,0.16)" />
        <rect x="1256" y="144" width="9" height="206" />
        <rect x="1160" y="232" width="210" height="9" />
        {/* stool + plant right bottom */}
        <rect x="1120" y="560" width="270" height="18" rx="9" />
        <rect x="1160" y="578" width="56" height="130" rx="8" />
        <rect x="1300" y="578" width="56" height="130" rx="8" />
        <rect x="60" y="640" width="56" height="50" rx="10" />
        <ellipse cx="88" cy="614" rx="44" ry="28" />
      </g>
    </svg>
  );
}

export default function Home() {
  const [phase, setPhase] = useState(() => {
    try {
      return sessionStorage.getItem("fridge-open") ? "open" : "closed";
    } catch (e) {
      return "closed";
    }
  });
  const { play, startHum } = useSound();
  const navigate = useNavigate();

  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const ry = useSpring(useTransform(mx, [-1, 1], [-4, 4]), { stiffness: 55, damping: 14 });
  const rx = useSpring(useTransform(my, [-1, 1], [3, -3]), { stiffness: 55, damping: 14 });
  const bgx = useSpring(useTransform(mx, [-1, 1], [16, -16]), { stiffness: 40, damping: 18 });
  const bgy = useSpring(useTransform(my, [-1, 1], [8, -8]), { stiffness: 40, damping: 18 });

  // midnight flashlight — cursor is the torch, follows with a lazy spring
  const fx = useMotionValue(typeof window !== "undefined" ? window.innerWidth / 2 : 500);
  const fy = useMotionValue(typeof window !== "undefined" ? window.innerHeight * 0.42 : 350);
  const sfx = useSpring(fx, { stiffness: 150, damping: 24 });
  const sfy = useSpring(fy, { stiffness: 150, damping: 24 });
  const torchMask = useMotionTemplate`radial-gradient(circle 185px at ${sfx}px ${sfy}px, transparent 0 78px, rgba(12,22,58,0.30) 140px, rgba(10,18,48,0.72) 225px)`;
  const glowX = useTransform(sfx, (v) => v - 190);
  const glowY = useTransform(sfy, (v) => v - 190);

  const handleOpen = () => {
    if (phase !== "closed") return;
    setPhase("opening");
    play("clank");
    setTimeout(() => play("creak"), 160);
    setTimeout(() => play("coldAir"), 620);
    setTimeout(() => play("thud"), 1020);
    setTimeout(() => {
      startHum();
      try { sessionStorage.setItem("fridge-open", "1"); } catch (e) {}
      navigate("/fridge", { replace: true });
    }, 1280);
  };

  if (phase === "open") return <OpenFridge />;

  return (
    <div
      className="min-h-screen kitchen-bg overflow-hidden relative"
      onMouseMove={(e) => {
        mx.set((e.clientX / window.innerWidth) * 2 - 1);
        my.set((e.clientY / window.innerHeight) * 2 - 1);
        fx.set(e.clientX);
        fy.set(e.clientY);
      }}
      data-testid="fridge-closed-container"
    >
      {/* midnight kitchen silhouettes — only the flashlight finds them */}
      <KitchenWall />

      {/* the big shiny red button — top-level fixed, pinned just RIGHT of the fridge so it can never overlap it */}
      {phase === "closed" && (
        <div className="hidden lg:block fixed top-[42%] z-[70]" style={{ left: "calc(50% + 284px)" }} data-testid="open-button-wrap">
          <FridgeButton variant="open" onOpen={handleOpen} captionClass="text-[#FFF3D6]/90" />
        </div>
      )}

      {/* floating kitchen doodles, drift with cursor */}
      <motion.div className="absolute inset-0 pointer-events-none" style={{ x: bgx, y: bgy }}>
        <Scribble className="top-[12%] left-[8%] -rotate-12">
          <path d="M30 30 q-10 4 -8 12 q4 4 10 0 q8 -4 6 -10" />
          <path d="M34 40 q34 6 44 34 q2 8 -6 8 q-30 -8 -44 -34" />
        </Scribble>
        <Scribble className="top-[18%] right-[10%] rotate-6">
          <circle cx="60" cy="60" r="26" />
          <path d="M52 54 q8 8 16 0 M50 66 q10 8 20 0" />
        </Scribble>
        <Scribble className="bottom-[16%] left-[12%] rotate-3">
          <ellipse cx="60" cy="64" rx="30" ry="22" />
          <path d="M30 62 q-6 2 0 6" />
        </Scribble>
        <Scribble className="bottom-[22%] right-[8%] -rotate-6">
          <path d="M26 40 l24 -12 24 12 v28 l-24 12 -24 -12 z" />
        </Scribble>
      </motion.div>

      <main className="relative min-h-screen flex flex-col items-center justify-center gap-6 pt-24 pb-12 px-4">
        <motion.p
          className="font-hand text-lg sm:text-xl text-ink/70 text-center whitespace-pre-line"
          initial={{ opacity: 0, y: -12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.15 }}
        >
          {SITE.hookline}
        </motion.p>

        <motion.div
          className="relative"
          style={{ rotateX: rx, rotateY: ry, transformPerspective: 1200, perspective: "1100px" }}
          initial={{ scale: 0.9, y: 30, opacity: 0 }}
          animate={{ scale: 1, y: 0, opacity: 1 }}
          transition={{ type: "spring", stiffness: 140, damping: 14, delay: 0.1 }}
        >
          {/* floor shadow */}
          <div className="absolute -bottom-10 left-1/2 -translate-x-1/2 w-[85%] h-8 bg-ink/15 rounded-full blur-md" />
          {/* fridge light leaking through the shut door — only readable in the dark */}
          {phase === "closed" && (
            <div
              className="absolute -inset-[7px] rounded-[42px] pointer-events-none"
              data-testid="door-light-leak"
              style={{ boxShadow: "0 0 38px 8px rgba(255,240,196,0.30), inset 0 0 20px rgba(255,240,196,0.14)" }}
            />
          )}

          <div className="relative w-[min(520px,90vw)] aspect-[400/600]">
            {/* shell */}
            <div className="absolute inset-0 rounded-[36px] border-[5px] border-ink fridge-shell shadow-[0_16px_0_rgba(43,35,32,0.2)]" />
            {/* interior glimpse revealed as the door swings */}
            <motion.div
              className="absolute inset-[3px] rounded-[31px] overflow-hidden"
              initial={{ opacity: 0 }}
              animate={{ opacity: phase === "closed" ? 0 : 1 }}
              transition={{ delay: 0.4, duration: 0.5 }}
            >
              <div className="absolute inset-0 bg-[#FBF6EA]" />
              <div className="absolute inset-x-0 top-0 h-2/3 light-glow" />
              <div className="absolute left-[20%] top-[45%] w-10 h-10 rounded-full bg-[#FF8FB1] opacity-70" />
              <div className="absolute left-[55%] top-[60%] w-8 h-8 rounded-full bg-[#7FBF4D] opacity-70" />
            </motion.div>
            {/* feet */}
            <div className="absolute -bottom-4 left-8 w-10 h-6 bg-[#8A9099] border-[3px] border-ink rounded-b-lg" />
            <div className="absolute -bottom-4 right-8 w-10 h-6 bg-[#8A9099] border-[3px] border-ink rounded-b-lg" />
            <DoorFront phase={phase} onOpen={handleOpen} />
          </div>

          {/* cold puffs */}
          {phase === "opening" &&
            [0, 1, 2].map((i) => (
              <motion.div
                key={i}
                className="absolute rounded-full bg-white/70 blur-sm pointer-events-none"
                style={{ width: 40 + i * 18, height: 40 + i * 18, left: `${28 + i * 20}%`, top: "42%" }}
                initial={{ opacity: 0, scale: 0.4, y: 0 }}
                animate={{ opacity: [0, 0.85, 0], scale: [0.4, 1.5, 2.1], y: [-12, -60, -115] }}
                transition={{ duration: 1.15, delay: 0.55 + i * 0.14 }}
              />
            ))}

          {/* fridge light spilling out onto the floor */}
          {phase === "opening" && (
            <motion.div
              className="absolute left-1/2 -translate-x-1/2 top-[99%] w-[220%] h-[52vh] pointer-events-none z-[65]"
              data-testid="fridge-light-beam"
              style={{
                background:
                  "linear-gradient(180deg, rgba(255,244,205,0.6) 0%, rgba(255,244,205,0.18) 55%, transparent 80%)",
                clipPath: "polygon(30% 0, 70% 0, 100% 100%, 0% 100%)",
              }}
              initial={{ opacity: 0 }}
              animate={{ opacity: [0, 0.95, 0] }}
              transition={{ duration: 1.35, times: [0, 0.45, 1], delay: 0.35 }}
            />
          )}
        </motion.div>

        {phase === "closed" && (
          <div className="lg:hidden relative z-[70]">
            <FridgeButton variant="open" onOpen={handleOpen} captionClass="text-[#FFF3D6]/90" />
          </div>
        )}

        {phase === "closed" && <Hint className="text-center">POV: it's midnight. you're hungry. shine your light on the fridge ↑</Hint>}
      </main>

      {/* warm torch glow under the darkness */}
      {phase === "closed" && (
        <motion.div
          className="fixed z-[55] w-[380px] h-[380px] rounded-full pointer-events-none"
          data-testid="flashlight-glow"
          style={{
            x: glowX,
            y: glowY,
            background: "radial-gradient(circle, rgba(255,238,190,0.22) 0%, rgba(255,238,190,0.08) 45%, transparent 70%)",
            mixBlendMode: "screen",
          }}
        />
      )}

      {/* midnight darkness with a torch hole */}
      <motion.div
        className="fixed inset-0 z-[60] pointer-events-none"
        data-testid="night-overlay"
        style={{
          WebkitMaskImage: torchMask,
          maskImage: torchMask,
          background: "linear-gradient(180deg, rgba(13,24,64,0.96) 0%, rgba(9,16,46,0.96) 100%)",
        }}
        initial={false}
        animate={{ opacity: phase === "opening" ? 0 : 1 }}
        transition={{ duration: phase === "opening" ? 0.85 : 0.4, delay: phase === "opening" ? 0.35 : 0 }}
      />

      {/* fridge light floods the room as the door swings */}
      {phase === "opening" && (
        <motion.div
          className="fixed inset-0 z-[70] pointer-events-none"
          data-testid="fridge-light-burst"
          initial={{ opacity: 0 }}
          animate={{ opacity: [0, 1, 0] }}
          transition={{ duration: 1.25, times: [0, 0.5, 1], delay: 0.3 }}
          style={{
            background:
              "radial-gradient(65% 65% at 50% 46%, rgba(255,248,220,0.98) 0%, rgba(255,244,205,0.5) 55%, transparent 80%)",
          }}
        />
      )}
    </div>
  );
}
